import { useRef, useCallback, useState, useEffect, type RefObject, type CSSProperties } from "react";
import type { PageIndicator } from "@shared/schema";

type CursorType = 'grab' | 'grabbing' | 'pointer' | 'help' | 'move' | 'zoom-in';

interface ImageViewerState {
  scale: number;
  offsetX: number;
  offsetY: number;
  minScale: number;
  maxScale: number;
  baseWidth: number;
  baseHeight: number;
  baseOffsetX: number;
  baseOffsetY: number;
  isDragging: boolean;
  isDraggingIndicator: boolean;
  draggedIndicator: PageIndicator | null;
  dragPreviewPosition: { x: number; y: number } | null;
  currentCursor: CursorType;
  hoveringOverIndicator: boolean;
  hoveredIndicatorId: string | null;
}

interface ImageViewerHook {
  containerRef: RefObject<HTMLDivElement>;
  imageRef: RefObject<HTMLImageElement>;
  state: ImageViewerState;
  imageStyle: CSSProperties;
  handleWheel: (e: React.WheelEvent) => void;
  handlePointerDown: (e: React.PointerEvent) => void;
  handlePointerMove: (e: React.PointerEvent) => void;
  handlePointerUp: (e: React.PointerEvent) => void;
  handleMouseMove: (e: React.MouseEvent) => void;
  programmaticZoom: (direction: number) => void;
  resetViewerState: () => void;
  imageToViewportCoords: (imageX: number, imageY: number) => { x: number; y: number };
  viewportToImageCoords: (viewportX: number, viewportY: number) => { x: number; y: number };
  getIndicatorAtPosition: (viewportX: number, viewportY: number) => PageIndicator | null;
  isDraggingIndicator: () => boolean;
  getDraggedIndicator: () => PageIndicator | null;
  getDragPreviewPosition: () => { x: number; y: number } | null;
  getDraggedIndicatorImageCoords: () => { x: number; y: number } | null;
  getCurrentCursor: () => CursorType;
  wasActualClick: () => boolean;
  handlePointerClick: () => { x: number; y: number } | null;
}

const INDICATOR_HIT_RADIUS = 20;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function useImageViewer(
  imageWidth: number,
  imageHeight: number,
  indicators: PageIndicator[],
  pendingIndicator?: { x: number; y: number; number: number } | null,
  onIndicatorDragEnd?: (indicatorId: string, imageX: number, imageY: number) => void,
  isFullscreen?: boolean
): ImageViewerHook {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  
  const [state, setState] = useState<ImageViewerState>({
    scale: 1,
    offsetX: 0,
    offsetY: 0,
    minScale: 1,
    maxScale: 3,
    baseWidth: imageWidth,
    baseHeight: imageHeight,
    baseOffsetX: 0,
    baseOffsetY: 0,
    isDragging: false,
    isDraggingIndicator: false,
    draggedIndicator: null,
    dragPreviewPosition: null,
    currentCursor: 'help',
    hoveringOverIndicator: false,
    hoveredIndicatorId: null,
  });

  const lastPointerPos = useRef<{ x: number; y: number } | null>(null);
  const startPointerPos = useRef<{ x: number; y: number } | null>(null);
  const hasActuallyDragged = useRef(false);
  const indicatorDragOffset = useRef<{ x: number; y: number } | null>(null);
  const lastClickPosition = useRef<{ x: number; y: number } | null>(null);
  const validClickForIndicator = useRef(false);
  const pointerIdRef = useRef<number | null>(null);
  const draggedIndicatorImageCoords = useRef<{ x: number; y: number } | null>(null);

  // Simple pan constraint: clamp to prevent gaps
  const constrainPan = useCallback((scale: number, offsetX: number, offsetY: number, containerWidth: number, containerHeight: number, baseWidth: number, baseHeight: number, baseOffsetX: number, baseOffsetY: number) => {
    const scaledWidth = baseWidth * scale;
    const scaledHeight = baseHeight * scale;

    // Adjusted offsets account for base positioning
    const totalOffsetX = baseOffsetX + offsetX;
    const totalOffsetY = baseOffsetY + offsetY;

    // Only constrain on axes where the image exceeds the container
    let constrainedTotalX = totalOffsetX;
    let constrainedTotalY = totalOffsetY;

    if (scaledWidth > containerWidth) {
      // Image is wider than container - allow panning, constrain to edges
      constrainedTotalX = clamp(totalOffsetX, containerWidth - scaledWidth, 0);
    } else {
      // Image fits within container width - keep it centered
      constrainedTotalX = (containerWidth - scaledWidth) / 2;
    }

    if (scaledHeight > containerHeight) {
      // Image is taller than container - allow panning, constrain to edges
      constrainedTotalY = clamp(totalOffsetY, containerHeight - scaledHeight, 0);
    } else {
      // Image fits within container height - keep it centered
      constrainedTotalY = (containerHeight - scaledHeight) / 2;
    }

    return { 
      offsetX: constrainedTotalX - baseOffsetX,
      offsetY: constrainedTotalY - baseOffsetY
    };
  }, []);

  // Calculate dynamic maxScale from native image dimensions
  const calculateMaxScale = useCallback(() => {
    const image = imageRef.current;
    if (image && image.complete && image.naturalWidth && image.naturalHeight) {
      const nativeToDisplayRatioX = image.naturalWidth / imageWidth;
      const nativeToDisplayRatioY = image.naturalHeight / imageHeight;
      const nativeMaxScale = Math.min(nativeToDisplayRatioX, nativeToDisplayRatioY);
      return Math.max(3, nativeMaxScale);
    }
    return 3;
  }, [imageWidth, imageHeight]);

  // Update maxScale when image loads
  useEffect(() => {
    const image = imageRef.current;
    if (!image) return;

    const handleImageLoad = () => {
      const dynamicMaxScale = calculateMaxScale();
      setState(prev => ({
        ...prev,
        maxScale: dynamicMaxScale,
      }));
    };

    if (image.complete) {
      handleImageLoad();
    } else {
      image.addEventListener('load', handleImageLoad);
      return () => image.removeEventListener('load', handleImageLoad);
    }
  }, [calculateMaxScale]);

  // Calculate fitted size (cover scale) on mount and resize
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !imageWidth || !imageHeight) return;

    const updateFit = () => {
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      
      if (containerWidth === 0 || containerHeight === 0) return;
      
      // Calculate scale: contain (fit within) for fullscreen, cover (fill) for normal mode
      const scaleX = containerWidth / imageWidth;
      const scaleY = containerHeight / imageHeight;
      const fitScale = isFullscreen ? Math.min(scaleX, scaleY) : Math.max(scaleX, scaleY);
      
      const fittedWidth = imageWidth * fitScale;
      const fittedHeight = imageHeight * fitScale;
      
      // Center the fitted image
      const fittedOffsetX = (containerWidth - fittedWidth) / 2;
      const fittedOffsetY = (containerHeight - fittedHeight) / 2;

      setState(prev => ({
        ...prev,
        baseWidth: fittedWidth,
        baseHeight: fittedHeight,
        baseOffsetX: fittedOffsetX,
        baseOffsetY: fittedOffsetY,
        minScale: 1, // scale 1 = fitted size
      }));
    };

    updateFit();

    const resizeObserver = new ResizeObserver(updateFit);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [imageWidth, imageHeight, isFullscreen]);

  // Coordinate conversion
  const imageToViewportCoords = useCallback((imageX: number, imageY: number) => {
    const x = (imageX / imageWidth) * state.baseWidth * state.scale + state.baseOffsetX + state.offsetX;
    const y = (imageY / imageHeight) * state.baseHeight * state.scale + state.baseOffsetY + state.offsetY;
    return { x, y };
  }, [state.scale, state.offsetX, state.offsetY, state.baseWidth, state.baseHeight, state.baseOffsetX, state.baseOffsetY, imageWidth, imageHeight]);

  const viewportToImageCoords = useCallback((viewportX: number, viewportY: number) => {
    const x = ((viewportX - state.baseOffsetX - state.offsetX) / (state.baseWidth * state.scale)) * imageWidth;
    const y = ((viewportY - state.baseOffsetY - state.offsetY) / (state.baseHeight * state.scale)) * imageHeight;
    
    return {
      x: Math.round(Math.max(0, Math.min(imageWidth, x))),
      y: Math.round(Math.max(0, Math.min(imageHeight, y))),
    };
  }, [state.scale, state.offsetX, state.offsetY, state.baseWidth, state.baseHeight, state.baseOffsetX, state.baseOffsetY, imageWidth, imageHeight]);

  // Find indicator at viewport position
  const getIndicatorAtPosition = useCallback((viewportX: number, viewportY: number): PageIndicator | null => {
    const allIndicators = pendingIndicator 
      ? [...indicators, { ...pendingIndicator, id: 'pending', status: 'open' as const }]
      : indicators;

    for (const indicator of allIndicators) {
      const indicatorViewport = imageToViewportCoords(indicator.x, indicator.y);
      const distance = Math.sqrt(
        Math.pow(viewportX - indicatorViewport.x, 2) + 
        Math.pow(viewportY - indicatorViewport.y, 2)
      );
      
      if (distance <= INDICATOR_HIT_RADIUS) {
        return indicator as PageIndicator;
      }
    }
    return null;
  }, [indicators, pendingIndicator, imageToViewportCoords]);

  // Wheel - disabled
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Pointer down
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const container = containerRef.current;
    if (!container) return;

    container.setPointerCapture(e.pointerId);
    pointerIdRef.current = e.pointerId;

    const rect = container.getBoundingClientRect();
    const viewportX = e.clientX - rect.left;
    const viewportY = e.clientY - rect.top;

    lastPointerPos.current = { x: e.clientX, y: e.clientY };
    startPointerPos.current = { x: e.clientX, y: e.clientY };
    hasActuallyDragged.current = false;

    const indicator = getIndicatorAtPosition(viewportX, viewportY);

    if (indicator && indicator.id !== 'pending') {
      const indicatorViewport = imageToViewportCoords(indicator.x, indicator.y);
      indicatorDragOffset.current = {
        x: viewportX - indicatorViewport.x,
        y: viewportY - indicatorViewport.y,
      };
      setState(prev => ({ 
        ...prev, 
        isDraggingIndicator: true, 
        draggedIndicator: indicator, 
        dragPreviewPosition: { x: viewportX, y: viewportY },
        currentCursor: 'move' 
      }));
    } else {
      // Only allow panning if zoomed in (scale > 1)
      if (state.scale > 1) {
        setState(prev => ({ ...prev, isDragging: true, currentCursor: 'grabbing' }));
      }
    }
  }, [getIndicatorAtPosition, imageToViewportCoords, state.scale]);

  // Pointer move
  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!lastPointerPos.current) return;

    const container = containerRef.current;
    if (!container) return;

    const deltaX = e.clientX - lastPointerPos.current.x;
    const deltaY = e.clientY - lastPointerPos.current.y;

    if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
      hasActuallyDragged.current = true;
    }

    if (state.isDragging) {
      const newOffsetX = state.offsetX + deltaX;
      const newOffsetY = state.offsetY + deltaY;
      const constrained = constrainPan(state.scale, newOffsetX, newOffsetY, container.clientWidth, container.clientHeight, state.baseWidth, state.baseHeight, state.baseOffsetX, state.baseOffsetY);
      
      setState(prev => ({
        ...prev,
        offsetX: constrained.offsetX,
        offsetY: constrained.offsetY,
      }));
    } else if (state.isDraggingIndicator && indicatorDragOffset.current) {
      const rect = container.getBoundingClientRect();
      const viewportX = e.clientX - rect.left;
      const viewportY = e.clientY - rect.top;
      
      const adjustedViewportX = viewportX - indicatorDragOffset.current.x;
      const adjustedViewportY = viewportY - indicatorDragOffset.current.y;
      const imageCoords = viewportToImageCoords(adjustedViewportX, adjustedViewportY);
      draggedIndicatorImageCoords.current = imageCoords;
      
      setState(prev => ({
        ...prev,
        dragPreviewPosition: { x: viewportX, y: viewportY },
      }));
    }

    lastPointerPos.current = { x: e.clientX, y: e.clientY };
  }, [state.isDragging, state.isDraggingIndicator, state.offsetX, state.offsetY, state.scale, state.baseWidth, state.baseHeight, state.baseOffsetX, state.baseOffsetY, constrainPan, viewportToImageCoords]);

  // Pointer up
  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    const container = containerRef.current;
    if (container && pointerIdRef.current !== null) {
      container.releasePointerCapture(pointerIdRef.current);
      pointerIdRef.current = null;
    }

    if (state.isDraggingIndicator && state.draggedIndicator && draggedIndicatorImageCoords.current && hasActuallyDragged.current) {
      const finalCoords = draggedIndicatorImageCoords.current;
      if (onIndicatorDragEnd) {
        onIndicatorDragEnd(state.draggedIndicator.id, finalCoords.x, finalCoords.y);
      }
    }

    if (!hasActuallyDragged.current && startPointerPos.current && container) {
      const rect = container.getBoundingClientRect();
      const viewportX = e.clientX - rect.left;
      const viewportY = e.clientY - rect.top;
      const imageCoords = viewportToImageCoords(viewportX, viewportY);
      
      lastClickPosition.current = imageCoords;
      validClickForIndicator.current = true;
    }

    setState(prev => ({
      ...prev,
      isDragging: false,
      isDraggingIndicator: false,
      draggedIndicator: null,
      dragPreviewPosition: null,
      currentCursor: prev.hoveringOverIndicator ? 'pointer' : (prev.scale > 1 ? 'grab' : 'help'),
    }));

    lastPointerPos.current = null;
    startPointerPos.current = null;
    hasActuallyDragged.current = false;
    indicatorDragOffset.current = null;
    draggedIndicatorImageCoords.current = null;
  }, [state.isDraggingIndicator, state.draggedIndicator, viewportToImageCoords, onIndicatorDragEnd]);

  // Mouse move for hover
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (state.isDragging || state.isDraggingIndicator) return;

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const viewportX = e.clientX - rect.left;
    const viewportY = e.clientY - rect.top;

    const indicator = getIndicatorAtPosition(viewportX, viewportY);
    const isHovering = !!indicator;
    const currentHoveredId = indicator?.id || null;
    
    const newCursor = isHovering ? 'pointer' : (state.scale > 1 ? 'grab' : 'help');

    if (state.hoveringOverIndicator !== isHovering || state.hoveredIndicatorId !== currentHoveredId || state.currentCursor !== newCursor) {
      setState(prev => ({
        ...prev,
        hoveringOverIndicator: isHovering,
        hoveredIndicatorId: currentHoveredId,
        currentCursor: newCursor,
      }));
    }
  }, [state.isDragging, state.isDraggingIndicator, state.hoveringOverIndicator, state.hoveredIndicatorId, state.currentCursor, state.scale, getIndicatorAtPosition]);

  // Programmatic zoom
  const programmaticZoom = useCallback((direction: number) => {
    const container = containerRef.current;
    if (!container) return;

    let newScale: number;
    if (direction > 0) {
      newScale = state.scale * 1.5;
    } else {
      newScale = state.scale / 1.5;
    }
    
    newScale = clamp(newScale, state.minScale, state.maxScale);
    
    // In fullscreen mode, always center the image without constraints
    if (isFullscreen) {
      const scaledWidth = state.baseWidth * newScale;
      const scaledHeight = state.baseHeight * newScale;
      
      // Center the image directly
      const newOffsetX = (container.clientWidth - scaledWidth) / 2 - state.baseOffsetX;
      const newOffsetY = (container.clientHeight - scaledHeight) / 2 - state.baseOffsetY;
      
      setState(prev => ({
        ...prev,
        scale: newScale,
        offsetX: newOffsetX,
        offsetY: newOffsetY,
      }));
    } else {
      // Normal mode: use zoom-toward-center with constraints
      const centerX = container.clientWidth / 2;
      const centerY = container.clientHeight / 2;
      
      const scaleRatio = newScale / state.scale;
      const newOffsetX = centerX - state.baseOffsetX - (centerX - state.baseOffsetX - state.offsetX) * scaleRatio - state.baseOffsetX;
      const newOffsetY = centerY - state.baseOffsetY - (centerY - state.baseOffsetY - state.offsetY) * scaleRatio - state.baseOffsetY;

      const constrained = constrainPan(newScale, newOffsetX, newOffsetY, container.clientWidth, container.clientHeight, state.baseWidth, state.baseHeight, state.baseOffsetX, state.baseOffsetY);

      setState(prev => ({
        ...prev,
        scale: newScale,
        offsetX: constrained.offsetX,
        offsetY: constrained.offsetY,
      }));
    }
  }, [state.scale, state.minScale, state.maxScale, state.offsetX, state.offsetY, state.baseWidth, state.baseHeight, state.baseOffsetX, state.baseOffsetY, constrainPan, isFullscreen]);

  // Click handler
  const handlePointerClick = useCallback(() => {
    if (validClickForIndicator.current && lastClickPosition.current) {
      const position = { ...lastClickPosition.current };
      validClickForIndicator.current = false;
      lastClickPosition.current = null;
      return position;
    }
    return null;
  }, []);

  // Reset viewer state to defaults (for fullscreen mode)
  const resetViewerState = useCallback(() => {
    setState(prev => ({
      ...prev,
      scale: 1,
      offsetX: 0,
      offsetY: 0,
    }));
  }, []);

  // Helper functions
  const wasActualClick = useCallback(() => !hasActuallyDragged.current, []);
  const isDraggingIndicator = useCallback(() => state.isDraggingIndicator, [state.isDraggingIndicator]);
  const getDraggedIndicator = useCallback(() => state.draggedIndicator, [state.draggedIndicator]);
  const getDragPreviewPosition = useCallback(() => state.dragPreviewPosition, [state.dragPreviewPosition]);
  const getDraggedIndicatorImageCoords = useCallback(() => draggedIndicatorImageCoords.current, []);
  const getCurrentCursor = useCallback(() => state.currentCursor, [state.currentCursor]);

  // Image style with fitted base size + zoom/pan transform
  const imageStyle: CSSProperties = {
    width: state.baseWidth,
    height: state.baseHeight,
    transform: `translate(${state.baseOffsetX + state.offsetX}px, ${state.baseOffsetY + state.offsetY}px) scale(${state.scale})`,
    transformOrigin: '0 0',
    transition: state.isDragging || state.isDraggingIndicator ? 'none' : 'transform 0.1s ease-out',
    position: 'absolute',
    top: 0,
    left: 0,
    userSelect: 'none',
    pointerEvents: 'none',
    imageRendering: 'crisp-edges',
  };

  return {
    containerRef,
    imageRef,
    state,
    imageStyle,
    handleWheel,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleMouseMove,
    programmaticZoom,
    resetViewerState,
    imageToViewportCoords,
    viewportToImageCoords,
    getIndicatorAtPosition,
    isDraggingIndicator,
    getDraggedIndicator,
    getDragPreviewPosition,
    getDraggedIndicatorImageCoords,
    getCurrentCursor,
    wasActualClick,
    handlePointerClick,
  };
}
